import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconInstallments from '@/assets/icons/heroIcons/Installments.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconSearch from '@/assets/icons/heroIcons/Search.svg';
import IconCursor from '@/assets/icons/heroIcons/Cursor-click.svg';
import IconPayment from '@/assets/icons/heroIcons/Deferred-billing-payment.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { Calculator } from '@/sections/installments/Calculator';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { useClientOnly } from '@/hooks/useClientOnly';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import { getSectionAnchorId } from '@/utils/anchors';
import { useProductRegistrationLink } from '@/hooks/useProductRegistrationLink';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(133);
  usePageView("loan");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["loan", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const registrationLink = useProductRegistrationLink("pujcka");
  const {
    isClient
  } = useClientOnly();
  let t1;
  if ($[1] !== registrationLink || $[2] !== t) {
    t1 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 5 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={LINKS().packages} testId="loanPackages" analyticsParam="loanPackages" /></Trans>
        } : {
          footer: <Button analyticsParam="loanRegistration" testId="loanRegistration" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={registrationLink}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/loan/cards/desktop/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/loan/cards/mobile/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[1] = registrationLink;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cards = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("seo.title");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("seo.description");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== pageBanner?.title || $[12] !== t) {
    t5 = pageBanner?.title ?? t("banner.title");
    $[11] = pageBanner?.title;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== pageBanner?.text || $[15] !== t) {
    t6 = pageBanner?.text ?? t("banner.text");
    $[14] = pageBanner?.text;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== isClient || $[18] !== registrationLink) {
    t7 = isClient ? <div id={getSectionAnchorId("calculator")} className="scroll-mt-24"><Calculator compact={true} type="loan" registrationLink={registrationLink} /></div> : <div className="min-h-[44rem] w-full md:min-h-[45rem] xl:min-h-[43rem]" />;
    $[17] = isClient;
    $[18] = registrationLink;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <Hero title={t5} text={t6} buttons={null} image={null} variant="mockupMobile" className="bg-blue-50 lg:pt-40!" contentClassName="xl:w-[35rem] xl:-translate-y-20 xl:justify-center 4xl:w-[38rem] 4xl:-translate-y-24" layoutClassName="xl:gap-8 4xl:gap-16" mediaClassName="mb-20 xl:mb-0 xl:max-w-[38.5rem] 4xl:mt-0 4xl:max-w-[43rem]" customImageContent={t7} />;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getSectionAnchorId("advantages");
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t) {
    t10 = t("advantages.title");
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <SectionHeadline className="mx-auto mb-10 max-w-192 px-0 md:mb-16" anchorKey="advantages.title">{t10}</SectionHeadline>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t("advantages.items.1.title");
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Link to={LINKS().packages} testId="loanPackages" analyticsParam="loanPackages" />;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = <Trans t={t} i18nKey="advantages.items.1.text">{t13}</Trans>;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <IconCursor />;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t12 || $[36] !== t14) {
    t16 = {
      title: t12,
      paragraph: t14,
      icon: t15
    };
    $[35] = t12;
    $[36] = t14;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("advantages.items.2.title");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = t("advantages.items.2.text");
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <IconSearch />;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t17 || $[44] !== t18) {
    t20 = {
      title: t17,
      paragraph: t18,
      icon: t19
    };
    $[43] = t17;
    $[44] = t18;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t) {
    t21 = t("advantages.items.3.title");
    $[46] = t;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("advantages.items.3.text");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <IconPayment />;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t21 || $[52] !== t22) {
    t24 = {
      title: t21,
      paragraph: t22,
      icon: t23
    };
    $[51] = t21;
    $[52] = t22;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t16 || $[55] !== t20 || $[56] !== t24) {
    t25 = <InfoBoxes items={[t16, t20, t24]} variant="basic" />;
    $[54] = t16;
    $[55] = t20;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Link to={`${LINKS().packages}#mini`} testId="loanPackages" analyticsParam="loanPackages" />;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Link to={LINKS().revolving} testId="loanRevolving" analyticsParam="loanRevolving" />;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = <Text variant="p4" className="text-center text-gray-800"><Trans t={t} i18nKey="advantages.text">{t26}{t27}</Trans></Text>;
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t("advantages.cta");
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== registrationLink || $[65] !== t29) {
    t30 = <Button to={registrationLink} testId="loanAdvantagesRegistration" analyticsParam="loanAdvantagesRegistration" size="lg" variant="primary" align="center">{t29}</Button>;
    $[64] = registrationLink;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t25 || $[68] !== t28 || $[69] !== t30) {
    t31 = <div className="flex flex-col gap-10 px-4 md:px-0 lg:gap-12">{t25}{t28}{t30}</div>;
    $[67] = t25;
    $[68] = t28;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t11 || $[72] !== t31) {
    t32 = <div className="relative z-10">{t11}{t31}</div>;
    $[71] = t11;
    $[72] = t31;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="bgCircle absolute top-12 right-0 h-[11.25rem] w-[11.25rem] translate-x-1/2 lg:top-0 lg:left-0 lg:h-125 lg:w-125 lg:-translate-x-1/4" />;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t32) {
    t34 = <AnimatedSection bgColor="bg-blue-100" id={t9}><AnimatedContent className="overflow-x-hidden">{t32}{t33}</AnimatedContent></AnimatedSection>;
    $[75] = t32;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = getSectionAnchorId("howItWorks.title");
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t) {
    t36 = t("howItWorks.title");
    $[78] = t;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== t36) {
    t37 = <div className="mx-auto max-w-192"><SectionHeadline className="mb-10 md:mb-16" anchorKey="howItWorks.title">{t36}</SectionHeadline></div>;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== cards) {
    t38 = <StackCards imagePosition="left" cards={cards} />;
    $[82] = cards;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== t37 || $[85] !== t38) {
    t39 = <AnimatedContent>{t37}{t38}</AnimatedContent>;
    $[84] = t37;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = LINKS();
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Link to="/caste-dotazy/dalsi-caste-otazky" analyticsParam="faqLoan" testId="faqLoan" />;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  let t42;
  if ($[89] !== t) {
    t42 = <AnimatedContent className="pt-20 pb-10 lg:pt-40 lg:pb-20"><FaqIntro link={t40.faq} text={<Trans i18nKey="faq.text" t={t}>{t41}</Trans>} /></AnimatedContent>;
    $[89] = t;
    $[90] = t42;
  } else {
    t42 = $[90];
  }
  let t43;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <IconInstallments className="fillButtonIcon" />;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== t) {
    t44 = t("core:products.installments");
    $[92] = t;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = LINKS();
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] !== t44) {
    t46 = {
      analyticsParam: "loanMoreInfoDeferredPayment",
      icon: t43,
      testId: "loanMoreInfoDeferredPayment",
      title: t44,
      to: t45.installments
    };
    $[95] = t44;
    $[96] = t46;
  } else {
    t46 = $[96];
  }
  let t47;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <IconMaxi className="fillButtonIcon" />;
    $[97] = t47;
  } else {
    t47 = $[97];
  }
  let t48;
  if ($[98] !== t) {
    t48 = t("core:products.revolving");
    $[98] = t;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  let t49;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = LINKS();
    $[100] = t49;
  } else {
    t49 = $[100];
  }
  let t50;
  if ($[101] !== t48) {
    t50 = {
      analyticsParam: "loanMoreInfoRevolvingApplication",
      icon: t47,
      testId: "loanMoreInfoRevolvingApplication",
      title: t48,
      to: t49.revolving
    };
    $[101] = t48;
    $[102] = t50;
  } else {
    t50 = $[102];
  }
  let t51;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <IconThird className="fillButtonIcon" />;
    $[103] = t51;
  } else {
    t51 = $[103];
  }
  let t52;
  if ($[104] !== t) {
    t52 = t("core:products.payInThree");
    $[104] = t;
    $[105] = t52;
  } else {
    t52 = $[105];
  }
  let t53;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = LINKS();
    $[106] = t53;
  } else {
    t53 = $[106];
  }
  let t54;
  if ($[107] !== t52) {
    t54 = {
      analyticsParam: "loanMoreInfoPayInThree",
      icon: t51,
      testId: "loanMoreInfoPayInThree",
      title: t52,
      to: t53.payInThree
    };
    $[107] = t52;
    $[108] = t54;
  } else {
    t54 = $[108];
  }
  let t55;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <IconCard className="fillButtonIcon" />;
    $[109] = t55;
  } else {
    t55 = $[109];
  }
  let t56;
  if ($[110] !== t) {
    t56 = t("core:products.card");
    $[110] = t;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = LINKS();
    $[112] = t57;
  } else {
    t57 = $[112];
  }
  let t58;
  if ($[113] !== t56) {
    t58 = {
      analyticsParam: "loanMoreInfoCard",
      icon: t55,
      testId: "loanMoreInfoCard",
      title: t56,
      to: t57.card
    };
    $[113] = t56;
    $[114] = t58;
  } else {
    t58 = $[114];
  }
  let t59;
  if ($[115] !== t46 || $[116] !== t50 || $[117] !== t54 || $[118] !== t58) {
    t59 = <AnimatedContent className="relative z-10"><MoreInfoSection buttons={[t46, t50, t54, t58]} /></AnimatedContent>;
    $[115] = t46;
    $[116] = t50;
    $[117] = t54;
    $[118] = t58;
    $[119] = t59;
  } else {
    t59 = $[119];
  }
  let t60;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== t42 || $[122] !== t59) {
    t61 = <div className="relative overflow-hidden">{t42}{t59}{t60}</div>;
    $[121] = t42;
    $[122] = t59;
    $[123] = t61;
  } else {
    t61 = $[123];
  }
  let t62;
  if ($[124] !== t39 || $[125] !== t61) {
    t62 = <AnimatedSection bgColor="bg-blue-100" className="pb-0!" id={t35}>{t39}{t61}</AnimatedSection>;
    $[124] = t39;
    $[125] = t61;
    $[126] = t62;
  } else {
    t62 = $[126];
  }
  let t63;
  if ($[127] !== menuPromoBanners || $[128] !== t34 || $[129] !== t4 || $[130] !== t62 || $[131] !== t8) {
    t63 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t8}{t34}{t62}</Layout>;
    $[127] = menuPromoBanners;
    $[128] = t34;
    $[129] = t4;
    $[130] = t62;
    $[131] = t8;
    $[132] = t63;
  } else {
    t63 = $[132];
  }
  return t63;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('pujcka');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('loan'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'loan']))
    }
  };
}) satisfies GetStaticProps<Props>;